import { lazy, Suspense } from 'react'
import { Routes, Route, Navigate } from 'react-router-dom'
import { useAuth } from './context/AuthContext'
import { LocationProvider } from './context/LocationContext'
import { SessionProvider } from './context/SessionContext'
import { ProtectedRoute, GuestRoute } from './components/ProtectedRoute'
import AppShell from './components/common/AppShell'
import Spinner from './components/common/Spinner'
import ErrorBoundary from './components/common/ErrorBoundary'

// Auth pages (lazy loaded - reduces initial bundle for returning users)
const LoginPage = lazy(() => import('./pages/auth/LoginPage'))
const RegisterPage = lazy(() => import('./pages/auth/RegisterPage'))
const ForgotPasswordPage = lazy(() => import('./pages/auth/ForgotPasswordPage'))
const PendingApprovalPage = lazy(() => import('./pages/auth/PendingApprovalPage'))
const TwoFactorSetupPage = lazy(() => import('./pages/auth/TwoFactorSetupPage'))
const TwoFactorVerifyPage = lazy(() => import('./pages/auth/TwoFactorVerifyPage'))

// Employee pages (lazy loaded)
const EmployeeDashboard = lazy(() => import('./pages/employee/EmployeeDashboard'))
const ProfilePage = lazy(() => import('./pages/employee/ProfilePage'))
const TimesheetPage = lazy(() => import('./pages/employee/TimesheetPage'))
const NotificationsPage = lazy(() => import('./pages/employee/NotificationsPage'))

// Admin pages (lazy loaded)
const AdminDashboard = lazy(() => import('./pages/admin/AdminDashboard'))
const NeedsAttentionPage = lazy(() => import('./pages/admin/NeedsAttentionPage'))
const WorkersPage = lazy(() => import('./pages/admin/WorkersPage'))
const WorkerDetailPage = lazy(() => import('./pages/admin/WorkerDetailPage'))
const SessionsPage = lazy(() => import('./pages/admin/SessionsPage'))
const SessionDetailPage = lazy(() => import('./pages/admin/SessionDetailPage'))
const ReportsPage = lazy(() => import('./pages/admin/ReportsPage'))
const AnalyticsPage = lazy(() => import('./pages/admin/AnalyticsPage'))
const BulkOperationsPage = lazy(() => import('./pages/admin/BulkOperationsPage'))
const AuditLogPage = lazy(() => import('./pages/admin/AuditLogPage'))
const ShiftManagementPage = lazy(() => import('./pages/admin/ShiftManagementPage'))
const LiveMapPage = lazy(() => import('./pages/admin/LiveMapPage'))

// Suspense fallback - shows brief spinner during initial lazy load
function PageLoader(): JSX.Element {
  return (
    <div className="flex items-center justify-center min-h-[200px]">
      <Spinner size="lg" />
    </div>
  )
}

function App(): JSX.Element {
  const { loading } = useAuth()

  if (loading) {
    return <Spinner.FullPage message="Loading Petru Drylining..." />
  }

  return (
    <ErrorBoundary>
      <Routes>
        {/* Guest routes (login, register) */}
        <Route
          path="/login"
          element={
            <GuestRoute>
              <Suspense fallback={<PageLoader />}>
                <LoginPage />
              </Suspense>
            </GuestRoute>
          }
        />
        <Route
          path="/register"
          element={
            <GuestRoute>
              <Suspense fallback={<PageLoader />}>
                <RegisterPage />
              </Suspense>
            </GuestRoute>
          }
        />
        <Route
          path="/forgot-password"
          element={
            <GuestRoute>
              <Suspense fallback={<PageLoader />}>
                <ForgotPasswordPage />
              </Suspense>
            </GuestRoute>
          }
        />

        {/* Pending approval - authenticated but not approved */}
        <Route
          path="/pending-approval"
          element={
            <ProtectedRoute requireApproval={false} require2FA={false}>
              <Suspense fallback={<PageLoader />}>
                <PendingApprovalPage />
              </Suspense>
            </ProtectedRoute>
          }
        />

        {/* 2FA Setup - authenticated but needs to set up 2FA */}
        <Route
          path="/setup-2fa"
          element={
            <ProtectedRoute requireApproval={false} require2FA={false}>
              <Suspense fallback={<PageLoader />}>
                <TwoFactorSetupPage />
              </Suspense>
            </ProtectedRoute>
          }
        />

        {/* 2FA Verification - during login flow */}
        <Route
          path="/verify-2fa"
          element={
            <Suspense fallback={<PageLoader />}>
              <TwoFactorVerifyPage />
            </Suspense>
          }
        />

        {/* Employee routes */}
        <Route
          element={
            <ProtectedRoute>
              <LocationProvider>
                <SessionProvider>
                  <AppShell />
                </SessionProvider>
              </LocationProvider>
            </ProtectedRoute>
          }
        >
          <Route path="/dashboard" element={<Suspense fallback={<PageLoader />}><EmployeeDashboard /></Suspense>} />
          <Route path="/profile" element={<Suspense fallback={<PageLoader />}><ProfilePage /></Suspense>} />
          <Route path="/timesheet" element={<Suspense fallback={<PageLoader />}><TimesheetPage /></Suspense>} />
          <Route path="/notifications" element={<Suspense fallback={<PageLoader />}><NotificationsPage /></Suspense>} />
        </Route>

        {/* Admin routes (lazy loaded) */}
        <Route
          element={
            <ProtectedRoute requireAdmin={true}>
              <AppShell />
            </ProtectedRoute>
          }
        >
          <Route
            path="/admin"
            element={
              <Suspense fallback={<PageLoader />}>
                <AdminDashboard />
              </Suspense>
            }
          />
          <Route
            path="/admin/attention"
            element={
              <Suspense fallback={<PageLoader />}>
                <NeedsAttentionPage />
              </Suspense>
            }
          />
          <Route
            path="/admin/workers"
            element={
              <Suspense fallback={<PageLoader />}>
                <WorkersPage />
              </Suspense>
            }
          />
          <Route
            path="/admin/workers/:userId"
            element={
              <Suspense fallback={<PageLoader />}>
                <WorkerDetailPage />
              </Suspense>
            }
          />
          <Route
            path="/admin/sessions"
            element={
              <Suspense fallback={<PageLoader />}>
                <SessionsPage />
              </Suspense>
            }
          />
          <Route
            path="/admin/sessions/:sessionId"
            element={
              <Suspense fallback={<PageLoader />}>
                <SessionDetailPage />
              </Suspense>
            }
          />
          <Route
            path="/admin/reports"
            element={
              <Suspense fallback={<PageLoader />}>
                <ReportsPage />
              </Suspense>
            }
          />
          <Route
            path="/admin/analytics"
            element={
              <Suspense fallback={<PageLoader />}>
                <AnalyticsPage />
              </Suspense>
            }
          />
          <Route
            path="/admin/bulk-operations"
            element={
              <Suspense fallback={<PageLoader />}>
                <BulkOperationsPage />
              </Suspense>
            }
          />
          <Route
            path="/admin/audit-log"
            element={
              <Suspense fallback={<PageLoader />}>
                <AuditLogPage />
              </Suspense>
            }
          />
          <Route
            path="/admin/shifts"
            element={
              <Suspense fallback={<PageLoader />}>
                <ShiftManagementPage />
              </Suspense>
            }
          />
          <Route
            path="/admin/live-map"
            element={
              <Suspense fallback={<PageLoader />}>
                <LiveMapPage />
              </Suspense>
            }
          />
        </Route>

        {/* Default redirect */}
        <Route path="/" element={<Navigate to="/login" replace />} />
        <Route path="*" element={<Navigate to="/login" replace />} />
      </Routes>
    </ErrorBoundary>
  )
}

export default App
